<template>
<div class="mp-root" :style="themePageStyle">

  <nav class="mp-topbar"><div class="container d-flex align-items-center justify-content-between py-2">
    <div class="d-flex align-items-center" style="gap:12px;">
      <back-button class="me-2" />
      <a href="javascript:void(0)" @click="$router.push('/dashboard')"><img src="/assets/images/flockr_logo.png" alt="Flockr" style="height:34px;" /></a>
      <span class="fw-semibold" style="color:var(--fm-h,#F5F0E8);font-size:15px;">My Products</span>
    </div>
    <div class="d-flex gap-2">
      <button class="btn btn-sm btn-outline-warning" @click="$router.push('/sell/shop')"><i class="mdi mdi-storefront-outline me-1"></i>Shop Profile</button>
      <button class="btn btn-sm btn-outline-warning" @click="$router.push('/sell/delivery')"><i class="mdi mdi-truck-delivery me-1"></i>Delivery Rates</button>
      <button class="btn btn-sm btn-outline-warning" @click="$router.push('/shop')"><i class="mdi mdi-eye-outline me-1"></i>View Shop</button>
      <button class="btn btn-sm btn-warning" @click="goCreate()"><i class="mdi mdi-plus me-1"></i>New Product</button>
    </div>
  </div></nav>

  <div class="container mp-container">

    <!-- Hero -->
    <div class="mb-4">
      <h4 class="fw-bold" style="color:var(--fm-h,#F5F0E8);">📦 My Agro-Input Products</h4>
      <p style="color:var(--fm-s,#6B6356);font-size:13px;">
        List feeds, supplements, vet drugs and equipment for sale on Flockr. Buyers add to cart and pay via M-Pesa — escrow holds funds until they confirm delivery.
      </p>
    </div>

    <!-- No-shop gate banner -->
    <div v-if="!loading && !hasShop" class="mp-shop-banner mb-4">
      <div class="d-flex align-items-center gap-3">
        <i class="mdi mdi-storefront" style="font-size:34px;color:#BFA164;"></i>
        <div class="flex-fill">
          <div style="color:var(--fm-h,#F5F0E8);font-weight:700;font-size:14px;">Set up your Agro Shop first</div>
          <div style="color:var(--fm-s,#6B6356);font-size:12px;">Create your shop profile (name, type, location) before you can list products. Takes a minute.</div>
        </div>
        <button class="btn btn-warning btn-sm" @click="$router.push('/sell/dashboard')"><i class="mdi mdi-arrow-right me-1"></i>Set up shop</button>
      </div>
    </div>

    <!-- Awaiting-verification gate banner -->
    <div v-else-if="!loading && hasShop && shopKyc!=='verified'" class="mp-shop-banner mb-4" :class="{'mp-shop-banner-danger':shopKyc==='rejected'}">
      <div class="d-flex align-items-center gap-3">
        <i class="mdi" :class="shopKyc==='rejected'?'mdi-alert':'mdi-clock-outline'" :style="{fontSize:'34px',color:shopKyc==='rejected'?'#FF6B6B':'#f1b44c'}"></i>
        <div class="flex-fill">
          <div style="color:var(--fm-h,#F5F0E8);font-weight:700;font-size:14px;">{{ shopKyc==='rejected' ? 'Verification rejected' : 'Shop awaiting verification' }}</div>
          <div style="color:var(--fm-s,#6B6356);font-size:12px;">{{ shopKyc==='rejected' ? 'Update your shop details for re-review before you can list products.' : 'Flockr is reviewing your shop. You can list products once it\'s verified — we\'ll notify you.' }}</div>
        </div>
        <button class="btn btn-sm" :class="shopKyc==='rejected'?'btn-outline-danger':'btn-outline-warning'" @click="$router.push('/sell/shop')"><i class="mdi mdi-pencil me-1"></i>{{ shopKyc==='rejected' ? 'Edit & resubmit' : 'View shop' }}</button>
      </div>
    </div>

    <!-- Stats strip -->
    <div v-if="hasShop && shopKyc==='verified'" class="row g-2 mb-4">
      <div class="col-6 col-md"><div class="mp-stat"><div class="mp-stat-l">Listed</div><div class="mp-stat-v" style="color:#BFA164;">{{ stats.total || 0 }}</div></div></div>
      <div class="col-6 col-md"><div class="mp-stat"><div class="mp-stat-l">Active</div><div class="mp-stat-v" style="color:#50C878;">{{ stats.active || 0 }}</div></div></div>
      <div class="col-6 col-md"><div class="mp-stat"><div class="mp-stat-l">Paused</div><div class="mp-stat-v" style="color:#f1b44c;">{{ stats.paused || 0 }}</div></div></div>
      <div class="col-6 col-md"><div class="mp-stat"><div class="mp-stat-l">Low stock</div><div class="mp-stat-v" style="color:#FF9800;">{{ stats.low_stock || 0 }}</div></div></div>
      <div class="col-6 col-md"><div class="mp-stat"><div class="mp-stat-l">Out of stock</div><div class="mp-stat-v" style="color:#FF6B6B;">{{ stats.out_of_stock || 0 }}</div></div></div>
      <div class="col-6 col-md"><div class="mp-stat"><div class="mp-stat-l">Total sold</div><div class="mp-stat-v" style="color:#BFA164;">{{ stats.total_sold || 0 }}</div></div></div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-5"><div class="spinner-border text-warning"></div></div>

    <!-- Empty -->
    <div v-else-if="hasShop && shopKyc==='verified' && !products.length" class="text-center py-5">
      <i class="mdi mdi-package-variant" style="font-size:64px;color:var(--fm-s,#3A3530);"></i>
      <h5 class="mt-3" style="color:var(--fm-h,#F5F0E8);">No products yet</h5>
      <p style="color:var(--fm-s,#6B6356);">Add your first feed, supplement, or vet drug to start selling.</p>
      <button class="btn btn-warning" @click="goCreate()">
        <i class="mdi mdi-plus me-1"></i>Add a Product
      </button>
    </div>

    <!-- Product list -->
    <div v-else-if="hasShop && shopKyc==='verified'" class="row g-3">
      <div class="col-md-6 col-lg-4" v-for="p in products" :key="p.id">
        <div class="mp-card">
          <div class="mp-cover" :style="p.primary_image_url ? 'background-image:url('+imgUrl(p.primary_image_url)+')' : ''">
            <i v-if="!p.primary_image_url" class="mdi mdi-package-variant" style="font-size:36px;color:var(--fm-s,#3A3530);"></i>
            <span class="mp-status" :class="'mp-st-'+p.status">{{ humanize(p.status) }}</span>
            <span v-if="String(p.is_featured) === '1'" class="mp-feat"><i class="mdi mdi-star"></i></span>
          </div>
          <div class="mp-body">
            <div class="mp-cat">{{ p.category_name || 'Uncategorised' }}</div>
            <div class="mp-name">{{ p.name }}</div>
            <div class="mp-price">KES {{ fNum(p.price) }}<span v-if="p.price_unit" class="mp-unit">/{{ p.price_unit }}</span></div>
            <div class="mp-meta">
              <span><i class="mdi mdi-package-variant me-1"></i>{{ p.stock_quantity }} in stock</span>
              <span v-if="p.total_sold"><i class="mdi mdi-cart-check me-1"></i>{{ p.total_sold }} sold</span>
              <span v-if="p.view_count"><i class="mdi mdi-eye-outline me-1"></i>{{ p.view_count }} views</span>
            </div>
            <div v-if="String(p.is_prescription_required) === '1'" class="mp-rx">
              <i class="mdi mdi-prescription me-1"></i>Prescription required
            </div>
            <div class="mp-actions">
              <button class="btn btn-sm btn-outline-warning flex-fill" @click="$router.push('/sell/product/edit/' + p.id)"><i class="mdi mdi-pencil me-1"></i>Edit</button>
              <button v-if="p.status === 'active'" class="btn btn-sm btn-outline-secondary" @click="setStatus(p, 'paused')" title="Pause"><i class="mdi mdi-pause"></i></button>
              <button v-else-if="p.status === 'paused'" class="btn btn-sm btn-outline-success" @click="setStatus(p, 'active')" title="Resume"><i class="mdi mdi-play"></i></button>
              <button class="btn btn-sm btn-outline-info" @click="$router.push('/shop/product/' + (p.slug || p.id))" title="View public page"><i class="mdi mdi-eye"></i></button>
              <button class="btn btn-sm btn-outline-danger" @click="remove(p)" title="Remove"><i class="mdi mdi-delete"></i></button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
</template>

<style>
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"]{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#F7F5F2;--fm-bg3:#F0EBE4;--fm-br:#E8E2DA}
html[data-theme="light"] body{background:#F7F5F2!important}
html[data-theme="light"] .mp-root{background:#F7F5F2!important;color:#1A1A1A!important}
html[data-theme="light"] .mp-topbar{background:rgba(255,255,255,.95)!important;border-bottom-color:#E8E2DA!important}
html[data-theme="light"] .mp-card,
html[data-theme="light"] .mp-stat,
html[data-theme="light"] .mp-shop-banner{background:#FFFFFF!important;border-color:#E8E2DA!important}
</style>

<style scoped>
.mp-root{background:var(--fm-bg2,#0A0A0A);min-height:100vh;color:var(--fm-h,#F5F0E8)}
.mp-topbar{position:sticky;top:0;z-index:100;padding:12px 0;background:var(--fm-bg1,#141414);backdrop-filter:blur(10px);border-bottom:1px solid var(--fm-br,#1E1E1E)}
.mp-container{padding-top:80px;padding-bottom:60px;max-width:1100px}
.mp-stat{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:10px;padding:10px 12px;text-align:center}
.mp-shop-banner{background:var(--fm-bg1,#141414);border:1px solid #BFA164;border-radius:12px;padding:16px}
.mp-shop-banner-danger{border-color:#FF6B6B}
.mp-stat-l{color:var(--fm-s,#6B6356);font-size:10px;text-transform:uppercase;letter-spacing:.4px;font-weight:600}
.mp-stat-v{font-weight:700;font-size:18px;margin-top:2px}

.mp-card{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:12px;overflow:hidden;height:100%;display:flex;flex-direction:column}
.mp-cover{height:140px;background:var(--fm-bg2,#0F0F0F);display:flex;align-items:center;justify-content:center;background-size:cover;background-position:center;position:relative}
.mp-status{position:absolute;top:8px;right:8px;font-size:10px;padding:2px 8px;border-radius:6px;text-transform:uppercase;letter-spacing:.4px;font-weight:600;background:rgba(0,0,0,.5);color:var(--fm-h,#F5F0E8)}
.mp-st-active{background:rgba(80,200,120,.18);color:#50C878}
.mp-st-paused{background:rgba(241,180,76,.18);color:#f1b44c}
.mp-st-draft{background:rgba(160,152,136,.18);color:#A09888}
.mp-st-out_of_stock{background:rgba(255,107,107,.18);color:#FF6B6B}
.mp-st-discontinued{background:rgba(255,107,107,.18);color:#FF6B6B}
.mp-feat{position:absolute;top:8px;left:8px;background:rgba(255,215,0,.18);color:#FFD700;font-size:11px;padding:2px 6px;border-radius:6px}
.mp-body{padding:14px;display:flex;flex-direction:column;flex:1}
.mp-cat{font-size:10px;color:#BFA164;text-transform:uppercase;letter-spacing:.4px;font-weight:600}
.mp-name{color:var(--fm-h,#F5F0E8);font-weight:700;font-size:14px;margin:4px 0;line-height:1.4;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
.mp-price{color:#BFA164;font-weight:700;font-size:16px}
.mp-unit{color:var(--fm-s,#6B6356);font-size:11px;font-weight:400;margin-left:2px}
.mp-meta{display:flex;flex-wrap:wrap;gap:10px;font-size:10px;color:var(--fm-m,#A09888);margin:8px 0}
.mp-rx{font-size:10px;color:#FF9800;background:rgba(255,152,0,.08);border:1px solid rgba(255,152,0,.2);border-radius:6px;padding:4px 8px;margin-bottom:8px}
.mp-actions{display:flex;gap:6px;margin-top:auto;padding-top:8px;border-top:1px dashed var(--fm-br,#1E1E1E)}

/* ===== MOBILE — feels-like-an-app ===== */
@media (max-width: 768px) {
  .mp-container { padding-top: 60px !important; padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px)) !important; padding-left: 12px !important; padding-right: 12px !important; }
  .mp-card { padding: 10px !important; border-radius: 10px !important; }
  .mp-name { font-size: 12px; }
  .mp-price { font-size: 14px; }
  .mp-meta { font-size: 9px; gap: 6px; }
  .mp-grid { gap: 8px; }
}
</style>

<script>
/* eslint-disable */
import { executeJson, flocker, uploads } from "@/api";

export default {
  name: "MyProductsPage",
  data(){return{
    darkMode:(document.documentElement.getAttribute('data-theme')||localStorage.getItem('flockr_theme')||'dark')!=='light',
    themeObserver:null,loading:true, hasShop:false, shopKyc:'',
    products:[], stats:{}
  }},
  computed:{
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'}
  },
  mounted(){
    this._applyThemeOnce();
    var self=this;
    this.themeObserver=new MutationObserver(function(){try{self._applyThemeOnce();}catch(e){}});
    this.themeObserver.observe(document.documentElement,{attributes:true,attributeFilter:['data-theme']});
    if(!sessionStorage.getItem('userId')){this.$router.push('/login?redirect=/sell/products');return}
    this.load();
  },
  beforeUnmount(){if(this.themeObserver){this.themeObserver.disconnect();this.themeObserver=null}},
  methods:{
    _applyThemeOnce(){
      var current=document.documentElement.getAttribute('data-theme');
      var desired=(localStorage.getItem('flockr_theme')||current||'dark');
      if(current!==desired) document.documentElement.setAttribute('data-theme',desired);
      this.darkMode=desired!=='light';
    },
    fNum(n){return Number(n||0).toLocaleString()},
    imgUrl(u){if(!u)return'';if(u.indexOf('http')===0)return u;return uploads+String(u).replace(/^uploads\//,'');},
    humanize(s){return (s||'').replace(/_/g,' ').replace(/\b\w/g,function(c){return c.toUpperCase()})},
    goCreate(){
      if(!this.hasShop){ this.$router.push('/sell/dashboard'); return; }
      if(this.shopKyc!=='verified'){ alert(this.shopKyc==='rejected' ? 'Your shop verification was rejected. Update your shop details for re-review before listing products.' : 'Your shop must be verified by Flockr before you can list products. We\'ll notify you once verification is complete.'); return; }
      this.$router.push('/sell/product/create');
    },
    async load(){
      this.loading=true;
      try{
        var sr=await executeJson({function:'getMyAgroShop',ownerId:sessionStorage.getItem('userId')},flocker);
        var shop=(sr.data&&sr.data.success&&sr.data.data&&sr.data.data.shop)||null;
        this.hasShop=!!shop;
        this.shopKyc=(shop&&shop.kyc_status)||'';
        var r=await executeJson({function:'getMyProducts',sellerId:sessionStorage.getItem('userId')},flocker);
        if(r.data&&r.data.success){
          this.products=(r.data.data&&r.data.data.products)||[];
          this.stats=(r.data.data&&r.data.data.stats)||{};
        }
      }catch(e){console.error(e)}
      this.loading=false;
    },
    async setStatus(p, newStatus){
      try{
        var r=await executeJson({
          function:'updateProduct',
          productId:p.id,
          sellerId:sessionStorage.getItem('userId'),
          status:newStatus
        },flocker);
        if(r.data&&r.data.success){await this.load()}
        else alert((r.data&&r.data.message)||'Failed to update');
      }catch(e){alert('Network error')}
    },
    async remove(p){
      if(!confirm("Remove '"+p.name+"' from your shop? This is permanent (the listing won't show to buyers)."))return;
      try{
        var r=await executeJson({
          function:'deleteProduct',
          productId:p.id,
          sellerId:sessionStorage.getItem('userId')
        },flocker);
        if(r.data&&r.data.success){await this.load()}
        else alert((r.data&&r.data.message)||'Failed to remove');
      }catch(e){alert('Network error')}
    }
  }
};
</script>
